// 화면 도구: 요소 만들기, 알림, 창, 뷰어, 행동 막대, 서버 부르기, 그림 줄이기
import { byId, CAMERAS, SCENES, TIMES, VENUE_TYPES } from "../shared/knowledge";
import { icon, type IconName } from "./icons";

export type Child = Node | string | number | null | undefined | false | Child[];
type Attrs = Record<string, any> | null;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Attrs, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === undefined || v === null || v === false) continue;
      if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2).toLowerCase(), v);
      else if (k === "class") el.className = String(v);
      else if (k === "style" && typeof v === "object") Object.assign(el.style, v);
      else if (k === "value" && "value" in el) (el as any).value = v;
      else if (k === "checked" && "checked" in el) (el as any).checked = Boolean(v);
      else if (v === true) el.setAttribute(k, "");
      else el.setAttribute(k, String(v));
    }
  }
  append(el, children);
  return el;
}

export function append(el: Node, children: Child[]) {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else el.appendChild(c instanceof Node ? c : document.createTextNode(String(c)));
  }
}

export function clear(el: Element) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

// ───────── 겹치는 층(창, 뷰어, 3D 창): Esc 는 맨 위 하나만 닫는다 ─────────

type Layer = { close: () => void; lock: boolean };
const layers: Layer[] = [];
const syncLock = () => (document.body.style.overflow = layers.some((l) => l.lock) ? "hidden" : "");

/** 층을 올린다. 돌려받은 함수를 부르면 내린다 */
export function pushLayer(close: () => void, lock = true): () => void {
  const layer = { close, lock };
  layers.push(layer);
  syncLock();
  return () => {
    const i = layers.indexOf(layer);
    if (i >= 0) layers.splice(i, 1);
    syncLock();
  };
}
const isTopLayer = (close: () => void) => layers[layers.length - 1]?.close === close;

/** 화면을 옮길 때 열린 창을 모두 닫는다 */
export function closeAllLayers() {
  for (const l of [...layers].reverse()) l.close();
}

document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape" || !layers.length || e.defaultPrevented) return;
  e.preventDefault();
  layers[layers.length - 1].close();
});

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/** Tab 이 창 안에서만 돌게 한다 */
export function trapTab(e: KeyboardEvent, box: HTMLElement) {
  if (e.key !== "Tab") return;
  const f = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
  if (!f.length) return;
  const first = f[0];
  const last = f[f.length - 1];
  if (e.shiftKey && (document.activeElement === first || !box.contains(document.activeElement))) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

const canHover = () => window.matchMedia("(hover: hover)").matches;

// ───────── 알림 ─────────

export function toast(msg: string, kind: "ok" | "err" | "info" = "info", ms?: number) {
  let box = document.getElementById("toasts");
  if (!box) {
    box = h("div", { id: "toasts" });
    document.body.appendChild(box);
  }
  while (box.children.length >= 3) box.firstElementChild!.remove();
  const life = ms ?? (kind === "err" ? 8000 : 4000);
  const t: HTMLElement = h(
    "div",
    { class: `toast ${kind}`, role: kind === "err" ? "alert" : "status" },
    icon(kind === "ok" ? "check-circle" : kind === "err" ? "alert" : "info", 18),
    h("div", { class: "msg" }, msg),
    kind === "err" ? h("button", { class: "toast-x", type: "button", "aria-label": "알림 닫기", onclick: () => t.remove() }, icon("x", 16)) : null,
  );
  box.appendChild(t);
  setTimeout(() => t.classList.add("out"), life);
  setTimeout(() => t.remove(), life + 350);
}

/** 화면 읽기 프로그램에만 알린다 (그림이 다 됐을 때 등) */
export function announce(text: string) {
  let el = document.getElementById("sr-live");
  if (!el) {
    el = h("div", { id: "sr-live", class: "sr-only", "aria-live": "polite" });
    document.body.appendChild(el);
  }
  el.textContent = "";
  const target = el;
  setTimeout(() => (target.textContent = text), 60);
}

// ───────── 창(모달), 시트, 확인 상자 ─────────

type ModalOpts = {
  size?: "sm" | "md" | "lg" | "xl"; // 440 / 560 / 800 / 1080
  sub?: string; // 제목 아래 한 줄
  foot?: Child | ((close: () => void) => Child); // 바닥 단추. 오른쪽 끝이 주 단추
  onClose?: () => void;
  cls?: string;
};

let seq = 0;

/** 창 하나. 닫는 함수를 돌려준다 */
export function modal(title: string, body: Node, opts: ModalOpts = {}): () => void {
  const prevFocus = document.activeElement as HTMLElement | null;
  const id = `dlg-${++seq}`;
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    pop();
    wrap.remove();
    opts.onClose?.();
    if (prevFocus && document.contains(prevFocus)) prevFocus.focus({ preventScroll: true });
  };
  const size = opts.size || "md";
  const foot = typeof opts.foot === "function" ? opts.foot(close) : opts.foot;
  const closeBtn = h("button", { class: "btn ghost small icon-only modal-x", type: "button", "aria-label": "닫기", onclick: close }, icon("x", 20));
  const box = h(
    "div",
    { class: `modal ${size}${opts.cls ? ` ${opts.cls}` : ""}`, role: "dialog", "aria-modal": "true", "aria-labelledby": id, tabindex: "-1" },
    h("div", { class: "modal-head" }, h("div", null, h("h2", { id }, title), opts.sub ? h("p", { class: "sub" }, opts.sub) : null), closeBtn),
    h("div", { class: "modal-body" }, body),
    foot ? h("div", { class: "modal-foot" }, foot) : null,
  );
  let downOnWrap = false;
  const wrap = h("div", { class: "modal-wrap", onmousedown: (e: Event) => (downOnWrap = e.target === wrap), onclick: (e: Event) => e.target === wrap && downOnWrap && close() }, box);
  box.addEventListener("keydown", (e) => trapTab(e, box));
  const pop = pushLayer(close);
  document.body.appendChild(wrap);
  requestAnimationFrame(() => {
    if (closed || box.contains(document.activeElement)) return;
    // 마우스가 있는 화면에서만 첫 입력 칸에 바로 들어간다 (폰에서는 자판이 바로 뜨지 않게). 숨은 칸은 건너뛴다
    const first = canHover()
      ? [...box.querySelectorAll<HTMLElement>(".modal-body input:not([type=checkbox]):not([disabled]), .modal-body textarea:not([disabled])")].find((el) => el.offsetParent !== null)
      : undefined;
    (first || box).focus({ preventScroll: true });
    if (!box.contains(document.activeElement)) box.focus({ preventScroll: true });
  });
  return close;
}

export function confirmBox(title: string, message: string, ok = "확인", o: { danger?: boolean } = {}): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: boolean) => {
      if (done) return;
      done = true;
      close();
      resolve(v);
    };
    const close = modal(title, h("p", { class: "pre" }, message), {
      size: "sm",
      onClose: () => finish(false),
      foot: [h("button", { class: "btn ghost", type: "button", onclick: () => finish(false) }, "취소"), h("button", { class: `btn ${o.danger ? "danger solid" : "primary"}`, type: "button", onclick: () => finish(true) }, ok)],
    });
  });
}

// ───────── 서버 ─────────

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown; raw?: Blob; rawType?: string } = {}): Promise<T> {
  const init: RequestInit = { method: opts.method || (opts.body !== undefined || opts.raw ? "POST" : "GET"), credentials: "same-origin", headers: {} };
  if (opts.raw) {
    init.body = opts.raw;
    (init.headers as any)["content-type"] = opts.rawType || opts.raw.type;
  } else if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
    (init.headers as any)["content-type"] = "application/json";
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    throw new ApiError(0, "인터넷 연결을 확인해 주세요.");
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new CustomEvent("auth-expired"));
    throw new ApiError(res.status, (data as any)?.error || `요청을 처리하지 못했어요. 잠시 뒤 다시 시도해 주세요 · 오류 ${res.status}`);
  }
  return data as T;
}

/** 단추를 누르는 동안 막고, 실패하면 알린다. timer 면 "label · 12초"처럼 지난 초를 센다 */
export async function busy<T>(btn: HTMLButtonElement | null, label: string, fn: () => Promise<T>, o: { timer?: boolean } = {}): Promise<T | undefined> {
  const before = btn?.innerHTML;
  // 글자 없이 돌기만 할 때도 화면 읽기 프로그램이 단추 이름을 읽을 수 있게 남긴다
  const hadLabel = btn?.getAttribute("aria-label") ?? null;
  let timer: any;
  if (btn) {
    if (!label && hadLabel === null) btn.setAttribute("aria-label", (btn.textContent || "").trim());
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
    const txt = h("span", null, label);
    btn.replaceChildren(h("span", { class: "spinner", "aria-hidden": "true" }), ...(label ? [txt] : []));
    if (o.timer && label) {
      const t0 = Date.now();
      timer = setInterval(() => (txt.textContent = `${label} · ${Math.round((Date.now() - t0) / 1000)}초`), 1000);
    }
  }
  try {
    return await fn();
  } catch (err) {
    toast(err instanceof Error ? err.message : String(err), "err");
    return undefined;
  } finally {
    clearInterval(timer);
    if (btn && before !== undefined) {
      btn.disabled = false;
      btn.removeAttribute("aria-busy");
      if (hadLabel === null) btn.removeAttribute("aria-label");
      btn.innerHTML = before;
    }
  }
}

// ───────── 작은 부품 ─────────

/** 단추 글자 + 비용. 예: withCost("시안 4장 그리기", 4) */
export function withCost(label: string, credits: number): Node[] {
  return [h("span", null, label), h("span", { class: "cost" }, icon("coins", 14), h("span", { "aria-hidden": "true" }, String(credits)), h("span", { class: "sr-only" }, ` ${credits} 크레딧`))];
}

/** 괄호 속 내부 용어를 뗀 이름 (예: '구조 확인용 (공간 합의)' → '구조 확인용') */
export const plainKo = (ko: string | undefined) => (ko || "").replace(/\s*\([^)]*\)\s*$/, "");

/** 화면에 보이는 카메라 이름. 화면 전체를 '장소'로 부르므로 '공간 그림'만 바꿔 보인다 (그림 모델에 가는 글은 그대로) */
export const CAMERAS_UI = CAMERAS.map((c) => (c.id === "same" ? { ...c, ko: c.ko.replace("공간 그림", "장소 그림") } : c));

/** '장면 · 시간 · 카메라' 한 줄 */
export const sceneLine = (s: { scene: string; time: string; camera: string }) => [plainKo(byId(SCENES, s.scene)?.ko), byId(TIMES, s.time)?.ko, byId(CAMERAS_UI, s.camera)?.ko].filter(Boolean).join(" · ");

/** 장소 종류 묶음: 행사에 맞는 묶음이 먼저, '기타'는 늘 맨 끝 (① 기본 정보와 3D 창이 같이 쓴다) */
export function venueGroups(sportsFirst: boolean) {
  const groups = [
    { id: "sports", ko: "경기장", list: VENUE_TYPES.filter((v) => v.group === "sports" && v.id !== "other") },
    { id: "general", ko: "일반 장소", list: VENUE_TYPES.filter((v) => v.group === "general" && v.id !== "other") },
  ];
  if (!sportsFirst) groups.reverse();
  return { groups, other: VENUE_TYPES.filter((v) => v.id === "other") };
}

/** 내려받는 파일 이름: 파일 이름에 못 쓰는 글자를 빼고, 그림의 확장자를 붙인다 */
export const fileName = (base: string, key: string) => `${base.replace(/[\\/:*?"<>|]+/g, "_").trim()}${key.match(/\.[a-z0-9]{2,5}$/i)?.[0] || ""}`;

/** 다시 그려도 키보드 초점을 같은 단추(data-k)에 돌려준다 */
export function redraw(box: HTMLElement, draw: () => void) {
  const a = document.activeElement as HTMLElement | null;
  const k = a && box.contains(a) ? a.dataset?.k : undefined;
  draw();
  if (k && !box.contains(document.activeElement)) box.querySelector<HTMLElement>(`[data-k="${CSS.escape(k)}"]`)?.focus({ preventScroll: true });
}

/** 백그라운드 작업이 20초 넘게 시작되지 않으면 직접 맡긴다 (작업마다 한 번만) */
const nudged = new Set<string>();
export function nudgeQueued(list: { id: string; status: string; createdAt: string }[]) {
  for (const c of list) {
    if (c.status !== "queued" || nudged.has(c.id) || Date.now() - new Date(c.createdAt).getTime() <= 20_000) continue;
    nudged.add(c.id);
    void api(`/api/jobs/run`, { body: { cutId: c.id } }).catch(() => {});
  }
}

/** 1차·2차 꼬리표 */
export function eyebrow(phase: "p1" | "p2"): HTMLElement {
  return phase === "p1" ? h("span", { class: "eyebrow p1" }, icon("stage", 14), "1차 · 무대 시안") : h("span", { class: "eyebrow p2" }, icon("film", 14), "2차 · 연출컷");
}

/** 카드 머리: 제목, 한 줄 설명(선택), 도움말(선택), 오른쪽 단추(선택) */
export function cardHead(title: Child, o: { sub?: Child; help?: string; right?: Child; eyebrow?: "p1" | "p2"; level?: "h2" | "h3" } = {}): HTMLElement {
  return h(
    "div",
    { class: "card-head" },
    h("div", { class: "card-head-text" }, o.eyebrow ? eyebrow(o.eyebrow) : null, h(o.level || "h2", { class: "head-title" }, title, o.help ? helpTip(o.help) : null), o.sub ? h("p", { class: "sub" }, o.sub) : null),
    o.right ? h("div", { class: "right" }, o.right) : null,
  );
}

/** 휴대폰에서만 접어 두는 칸. 접힌 동안은 제목·요약 한 줄 단추만 보이고, 누르면 펼친다. 데스크톱은 늘 펼친다 */
export function foldSm(content: HTMLElement, o: { title: string; icon?: IconName; summary: string; card?: boolean; class?: string }): HTMLElement {
  const head = h(
    "button",
    { class: `fold-sm-head${o.card ? " card" : ""}`, type: "button", "aria-expanded": "false" },
    o.icon ? icon(o.icon, 18) : null,
    h("span", { class: "fold-sm-text" }, h("b", null, o.title), h("span", { class: "fold-sm-sum one-line" }, o.summary)),
    icon("chevron-down", 16),
  );
  const box = h("div", { class: `fold-sm${o.class ? ` ${o.class}` : ""}` }, head, content);
  head.addEventListener("click", () => {
    box.classList.add("open");
    head.setAttribute("aria-expanded", "true");
    // 단추가 사라지므로 펼친 칸의 첫 입력으로 옮긴다
    content.querySelector<HTMLElement>("input, textarea, select, button")?.focus({ preventScroll: true });
  });
  return box;
}

/** 운영자만 보는 '전문가용' 접기 */
export function devDetails(summary: string, ...content: Child[]): HTMLElement {
  return h("details", { class: "dev" }, h("summary", null, summary), h("div", { class: "dev-body" }, content));
}

// ───────── 아래 고정 행동 막대 ─────────

export type BarOpts = {
  back?: { label: string; onclick: () => void } | null;
  status?: Child; // 한 줄. 막힌 이유도 여기에
  warn?: boolean; // 막힌 이유면 주황
  secondary?: HTMLElement[]; // 조용한/보조 단추 0~2개
  primary?: HTMLElement; // 채운 단추 하나
};

export function actionBar(o: BarOpts): HTMLElement {
  return h(
    "div",
    { class: "action-bar", role: "region", "aria-label": "다음 할 일" },
    h(
      "div",
      { class: "action-bar-inner" },
      o.back ? h("button", { class: "btn ghost bar-back", type: "button", "aria-label": `이전: ${o.back.label}`, onclick: o.back.onclick }, icon("chevron-left"), h("span", { class: "hide-sm" }, o.back.label)) : null,
      h("div", { class: `bar-status${o.warn ? " warn" : ""}`, "aria-live": "polite" }, o.warn ? icon("alert", 16) : null, h("span", { class: "bar-text" }, o.status ?? "")),
      h("div", { class: "bar-actions" }, o.secondary || [], o.primary || null),
    ),
  );
}

// ───────── 빈 상태, 진행 표시 ─────────

export function emptyState(o: { icon: IconName; title: string; body?: string; action?: HTMLElement | null; compact?: boolean }): HTMLElement {
  return h("div", { class: `empty-state${o.compact ? " compact" : ""}` }, h("div", { class: "empty-icon" }, icon(o.icon, o.compact ? 20 : 24)), h("h3", null, o.title), o.body ? h("p", null, o.body) : null, o.action || null);
}

/** 그림 생성 중·실패 표시. 그림 칸(.thumb) 안에 넣는다 */
export function genState(c: { status: string; createdAt: string; error?: string }, kind: string): HTMLElement {
  if (c.status === "failed") return h("div", { class: "gen err" }, icon("alert", 22), h("b", null, "만들지 못했어요"), c.error ? h("span", { class: "xs gen-err" }, c.error) : null, h("span", { class: "xs" }, "크레딧은 돌려드렸어요"));
  if (c.status === "queued") return h("div", { class: "gen" }, icon("clock", 22), h("span", null, "차례를 기다리는 중"));
  const t = h("span", { class: "num" }, "0:00");
  const tick = () => {
    const s = Math.max(0, Math.round((Date.now() - new Date(c.createdAt).getTime()) / 1000));
    t.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };
  // 화면에 붙은 뒤 떨어지면 멈춘다 (갤러리를 다시 그릴 때마다 새 카드가 생기므로). 5초 안에 한 번도 붙지 않아도 멈춘다
  let seen = false;
  let waits = 0;
  const timer = setInterval(() => {
    if (t.isConnected) {
      seen = true;
      tick();
    } else if (seen || ++waits > 5) clearInterval(timer);
  }, 1000);
  tick();
  return h("div", { class: "gen run" }, h("span", null, `${kind} 그리는 중 · `, t), h("span", { class: "xs" }, "보통 30초~1분"), h("span", { class: "progress-line" }));
}

// ───────── 나눔 단추 (하나만 고르기, 2~4개) ─────────

type SegOption = { id: string; ko: Child; icon?: IconName; title?: string; hint?: string };

/** 나눔 단추. allowNone 이면 고른 것을 다시 누를 때 비운다(onChange(null)). showHint 면 고른 것의 hint 를 아래 한 줄로.
 *  manual 이면 화살표는 초점만 옮기고 Space·Enter 로 고른다 (고를 때마다 서버에 가는 판정 등) */
export function segmented(
  options: SegOption[],
  value: string | null,
  onChange: (id: string | null) => void,
  label: string,
  o: { cls?: string; allowNone?: boolean; showHint?: boolean; disabled?: boolean; manual?: boolean } = {},
): HTMLElement {
  let cur = value;
  const seg = h("div", { class: `seg${o.cls ? ` ${o.cls}` : ""}`, role: "radiogroup", "aria-label": label });
  const hint = o.showHint ? h("p", { class: "chip-hint" }) : null;
  const btns = options.map((opt) =>
    h(
      "button",
      { type: "button", role: "radio", "data-v": opt.id, "data-k": `${label}:${opt.id}`, title: opt.title || null, disabled: o.disabled || null, onclick: () => pick(opt.id, true) },
      opt.icon ? icon(opt.icon, 16) : null,
      opt.ko,
    ),
  );
  const sync = () => {
    const hasCur = options.some((x) => x.id === cur);
    btns.forEach((b, i) => {
      const on = options[i].id === cur;
      b.setAttribute("aria-checked", on ? "true" : "false");
      // 고른 것이 없으면 첫 단추로 Tab 이 들어온다
      b.tabIndex = on || (!hasCur && i === 0) ? 0 : -1;
    });
    if (hint) {
      const t = options.find((x) => x.id === cur)?.hint || "";
      hint.textContent = t;
      hint.hidden = !t;
    }
  };
  const pick = (id: string, fromClick: boolean) => {
    const next = o.allowNone && fromClick && cur === id ? null : id;
    if (next === cur) return;
    cur = next;
    sync();
    onChange(cur);
  };
  seg.addEventListener("keydown", (e) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
    e.preventDefault();
    e.stopPropagation();
    const focused = btns.indexOf(document.activeElement as HTMLButtonElement);
    const i = o.manual && focused >= 0 ? focused : Math.max(0, options.findIndex((x) => x.id === cur));
    const d = e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 1;
    const n = (i + d + options.length) % options.length;
    if (!o.manual) pick(options[n].id, false);
    btns[n].focus();
  });
  append(seg, btns);
  sync();
  return hint ? h("div", { class: "seg-field" }, seg, hint) : seg;
}

/** 고르기 칩 묶음 (여러 개 중 고를 때). 하나만 고를 때 hint 가 있으면 고른 것의 hint 를 아래 한 줄로 */
export function chipGroup(list: { id: string; ko: Child; hint?: string }[], selected: string[], multi: boolean, onChange: (v: string[]) => void, label: string, o: { small?: boolean } = {}): HTMLElement {
  let cur = [...selected];
  const withHint = !multi && list.some((x) => x.hint);
  // 하나만 고르면 라디오 묶음으로 읽힌다
  const box = h("div", { class: "chips", role: multi ? "group" : "radiogroup", "aria-label": label });
  const hint = withHint ? h("p", { class: "chip-hint" }) : null;
  const btns = list.map((opt) =>
    h(
      "button",
      {
        type: "button",
        class: `chip-btn${o.small ? " small" : ""}`,
        role: multi ? null : "radio",
        "data-k": `${label}:${opt.id}`,
        title: opt.hint || null,
        onclick: () => {
          const on = cur.includes(opt.id);
          cur = multi ? (on ? cur.filter((x) => x !== opt.id) : [...cur, opt.id]) : [opt.id];
          sync();
          onChange(cur);
        },
      },
      opt.ko,
    ),
  );
  const sync = () => {
    btns.forEach((b, i) => {
      const on = cur.includes(list[i].id);
      b.classList.toggle("on", on);
      b.setAttribute(multi ? "aria-pressed" : "aria-checked", on ? "true" : "false");
    });
    if (hint) {
      const t = list.find((x) => cur.includes(x.id))?.hint || "";
      hint.textContent = t;
      hint.hidden = !t;
    }
  };
  // 화살표로 칩 사이를 옮겨 다닌다 (고르는 것은 Space·Enter)
  box.addEventListener("keydown", (e) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
    const i = btns.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    e.preventDefault();
    e.stopPropagation();
    const d = e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 1;
    btns[(i + d + btns.length) % btns.length].focus();
  });
  append(box, btns);
  sync();
  return hint ? h("div", { class: "chips-field" }, box, hint) : box;
}

// ───────── 더보기 메뉴(⋯), 도움말(?) ─────────

type MenuItem = { label: string; icon?: IconName; danger?: boolean; disabled?: boolean; onclick: () => void };

export function menu(items: MenuItem[], o: string | { label?: string; trigger?: HTMLButtonElement; head?: Child; cls?: string } = "더보기"): HTMLElement {
  const opts = typeof o === "string" ? { label: o } : o;
  const btn = opts.trigger || h("button", { class: "btn ghost small icon-only", type: "button", "aria-label": opts.label || "더보기" }, icon("more", 18));
  btn.setAttribute("aria-haspopup", "menu");
  btn.setAttribute("aria-expanded", "false");
  const wrap = h("div", { class: `menu-wrap${opts.cls ? ` ${opts.cls}` : ""}` }, btn);
  let pop: HTMLElement | null = null;
  const close = (refocus = false) => {
    if (!pop) return;
    pop.remove();
    pop = null;
    btn.setAttribute("aria-expanded", "false");
    document.removeEventListener("pointerdown", outside, true);
    document.removeEventListener("keydown", onKey, true);
    if (refocus) btn.focus();
  };
  const outside = (e: Event) => {
    if (!wrap.contains(e.target as Node)) close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (!pop) return;
    const its = [...pop.querySelectorAll<HTMLButtonElement>("button:not([disabled])")];
    const i = its.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      its[(i + 1) % its.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      its[(i - 1 + its.length) % its.length]?.focus();
    } else if (e.key === "Tab") close();
  };
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (pop) return close();
    pop = h(
      "div",
      { class: "menu-pop", role: "menu" },
      opts.head ? h("div", { class: "menu-head" }, opts.head) : null,
      items.map((it) =>
        h(
          "button",
          {
            type: "button",
            role: "menuitem",
            class: it.danger ? "danger" : null,
            disabled: it.disabled || null,
            onclick: (ev: Event) => {
              ev.stopPropagation();
              // 초점을 ⋯ 단추에 먼저 돌려준다 (뒤이어 열리는 확인 창이 닫히면 여기로 돌아온다)
              close(true);
              it.onclick();
            },
          },
          it.icon ? icon(it.icon, 16) : null,
          it.label,
        ),
      ),
    );
    wrap.append(pop);
    btn.setAttribute("aria-expanded", "true");
    const r = pop.getBoundingClientRect();
    if (r.bottom > window.innerHeight - 8 && r.height + 48 < r.top) pop.classList.add("up");
    if (r.left < 8) pop.classList.add("start");
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("keydown", onKey, true);
    pop.querySelector<HTMLElement>("button:not([disabled])")?.focus();
  });
  return wrap;
}

/** 도움말 단추. 누르면(또는 마우스를 올리면) 작은 말풍선이 열린다 */
export function helpTip(text: string): HTMLElement {
  const id = `tip-${++seq}`;
  const btn = h("button", { class: "help-btn", type: "button", "aria-label": "도움말", "aria-expanded": "false" }, icon("help", 16));
  const wrap = h("span", { class: "tip-wrap" }, btn);
  let pop: HTMLElement | null = null;
  let hoverT: any;
  const place = () => {
    if (!pop) return;
    const r = btn.getBoundingClientRect();
    const w = Math.min(280, window.innerWidth - 24);
    pop.style.maxWidth = `${w}px`;
    const pr = pop.getBoundingClientRect();
    let left = r.left + r.width / 2 - pr.width / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - pr.width - 12));
    let top = r.bottom + 6;
    if (top + pr.height > window.innerHeight - 8) top = Math.max(8, r.top - pr.height - 6);
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  };
  const close = () => {
    if (!pop) return;
    pop.remove();
    pop = null;
    btn.setAttribute("aria-expanded", "false");
    btn.removeAttribute("aria-describedby");
    document.removeEventListener("pointerdown", outside, true);
    document.removeEventListener("keydown", onKey, true);
    window.removeEventListener("scroll", close, true);
  };
  const outside = (e: Event) => {
    if (!wrap.contains(e.target as Node) && e.target !== pop) close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };
  const open = () => {
    if (pop) return;
    pop = h("div", { class: "tip-pop", role: "tooltip", id }, text);
    document.body.appendChild(pop);
    btn.setAttribute("aria-expanded", "true");
    btn.setAttribute("aria-describedby", id);
    place();
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", close, true);
  };
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (pop) close();
    else open();
  });
  wrap.addEventListener("mouseenter", () => {
    clearTimeout(hoverT);
    if (canHover()) open();
  });
  wrap.addEventListener("mouseleave", () => {
    if (canHover()) hoverT = setTimeout(close, 150);
  });
  return wrap;
}

// ───────── 크게 보기 (하나로 합친 뷰어) ─────────

export type ViewerItem = {
  src: string;
  title: string;
  meta?: string | (() => string); // 함수면 다시 그릴 때마다 새로 계산한다 (판정을 바꾸면 '승인본'이 바뀌므로)
  download?: string; // 파일 이름. 있으면 내려받기 단추
  checker?: boolean; // 투명 PNG(로고·에셋)면 체크 무늬 바탕
  actions?: (ctl: { close: () => void; refresh: () => void }) => Child; // 아래 줄 (판정, 고치기 …)
  details?: () => Child; // 운영자만: '자세히'
};

export function openViewer(items: ViewerItem[], start = 0): () => void {
  if (!items.length) return () => {};
  let i = Math.max(0, Math.min(start, items.length - 1));
  const prevFocus = document.activeElement as HTMLElement | null;
  const tid = `viewer-${++seq}`;
  const titleEl = h("span", { class: "title", id: tid });
  const metaEl = h("span", { class: "meta" });
  const countEl = h("span", { class: "count num" });
  const dl = h("a", { class: "btn icon-only", "aria-label": "내려받기", title: "내려받기" }, icon("download", 20));
  const closeBtn = h("button", { class: "btn icon-only", type: "button", "aria-label": "닫기", onclick: () => close() }, icon("x", 20));
  const img = h("img", { alt: "" });
  const prev = h("button", { class: "viewer-nav prev", type: "button", "aria-label": "이전 그림", onclick: () => move(-1) }, icon("chevron-left", 24));
  const next = h("button", { class: "viewer-nav next", type: "button", "aria-label": "다음 그림", onclick: () => move(1) }, icon("chevron-right", 24));
  const stage = h("div", { class: "viewer-stage" }, img, prev, next);
  const bottom = h("div", { class: "viewer-bottom" });
  const el = h(
    "div",
    { class: "viewer", role: "dialog", "aria-modal": "true", "aria-labelledby": tid, tabindex: "-1" },
    h("div", { class: "viewer-top" }, h("div", { class: "viewer-title" }, titleEl, metaEl), countEl, dl, closeBtn),
    stage,
    bottom,
  );
  function draw() {
    const it = items[i];
    img.src = it.src;
    img.alt = it.title;
    img.classList.toggle("checker", Boolean(it.checker));
    titleEl.textContent = it.title;
    metaEl.textContent = (typeof it.meta === "function" ? it.meta() : it.meta) || "";
    countEl.textContent = items.length > 1 ? `${i + 1} / ${items.length}` : "";
    dl.hidden = !it.download;
    if (it.download) {
      dl.href = it.src;
      dl.setAttribute("download", it.download);
    }
    prev.hidden = next.hidden = items.length < 2;
    clear(bottom);
    append(bottom, [it.actions ? h("div", { class: "viewer-actions" }, it.actions({ close, refresh: draw })) : null, it.details ? h("details", { class: "dev viewer-dev" }, h("summary", null, "자세히"), h("div", { class: "dev-body" }, it.details())) : null]);
    bottom.hidden = !bottom.childNodes.length;
  }
  const move = (d: number) => {
    if (items.length < 2) return;
    i = (i + d + items.length) % items.length;
    draw();
  };
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    pop();
    el.remove();
    if (prevFocus && document.contains(prevFocus)) prevFocus.focus({ preventScroll: true });
  };
  el.addEventListener("keydown", (e) => {
    trapTab(e, el);
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || !isTopLayer(close)) return;
    if (e.key === "ArrowLeft") move(-1);
    else if (e.key === "ArrowRight") move(1);
  });
  // 바깥 어두운 곳을 누를 때만 닫는다 (그림을 누르면 닫히지 않는다)
  stage.addEventListener("click", (e) => e.target === stage && close());
  // 폰: 좌우로 밀어 넘기기
  let x0: number | null = null;
  stage.addEventListener("pointerdown", (e) => (x0 = e.clientX));
  stage.addEventListener("pointerup", (e) => {
    if (x0 === null) return;
    const dx = e.clientX - x0;
    x0 = null;
    if (Math.abs(dx) > 50) move(dx < 0 ? 1 : -1);
  });
  const pop = pushLayer(close);
  document.body.appendChild(el);
  draw();
  el.focus({ preventScroll: true });
  return close;
}

// ───────── 그림·파일 ─────────

export const mediaUrl = (key: string) => `/api/media/${key.split("/").map(encodeURIComponent).join("/")}`;

/** 올리기 전에 그림을 줄인다: 긴 변 2400px, JPEG. 서버 한도(4.5MB) 아래로 */
export async function shrinkImage(file: File, maxSide = 2400): Promise<{ blob: Blob; w: number; h: number }> {
  const bmp = await createImageBitmap(file);
  let side = maxSide;
  for (let i = 0; i < 4; i++) {
    const scale = Math.min(1, side / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale);
    const hh = Math.round(bmp.height * scale);
    const c = document.createElement("canvas");
    c.width = w;
    c.height = hh;
    const ctx = c.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, hh);
    ctx.drawImage(bmp, 0, 0, w, hh);
    const blob: Blob = await new Promise((r, j) => c.toBlob((b) => (b ? r(b) : j(new Error("그림을 줄이지 못했어요. 다른 그림으로 해 주세요."))), "image/jpeg", 0.88));
    if (blob.size < 3.8 * 1024 * 1024) return { blob, w, h: hh };
    side = Math.round(side * 0.75);
  }
  throw new Error("그림이 너무 커서 줄이지 못했어요. 다른 그림으로 해 주세요.");
}

export function pickFiles(multiple = true): Promise<File[]> {
  return new Promise((resolve) => {
    const input = h("input", { type: "file", accept: "image/jpeg,image/png,image/webp", multiple, style: { display: "none" } });
    input.addEventListener("change", () => {
      resolve(Array.from(input.files || []));
      input.remove();
    });
    document.body.appendChild(input);
    input.click();
  });
}

export const fmtDate = (s: string) => {
  const d = new Date(s);
  return `${d.getMonth() + 1}월 ${d.getDate()}일 ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export const fmtDay = (s: string) => {
  const d = new Date(s);
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
};

export function debounce<A extends any[]>(fn: (...a: A) => void, ms: number) {
  let t: any;
  return (...a: A) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
}
